import { Col, Icon } from 'antd';
import PinnedAlertIcon from 'common-components/pinned-alerts/PinnedAlertIcon';
import { ActivityRecordStatusTag, BookingTypeIcon, StatusTag } from 'common-components/tags';
import TextTag from 'common-components/tags/TextTag';
import { TimezoneIndicator } from 'common-components/timezone';
import { Text } from 'common-components/typography';
import UserAvatar from 'common-components/user-avatar';
import { Broadcast, CheckCircle, Clock, Timer } from '@good/icons';
import { IBooking } from 'interfaces/booking-interfaces';
import { IFilter } from 'interfaces/filter-interfaces';
import { timeZone } from 'interfaces/timezone-type';
import _ from 'lodash';
import moment from 'moment-timezone';
import React, { PropsWithChildren, PureComponent } from 'react';
import { Link } from 'react-router-dom';
import CommonUtils from 'utilities/common-utils';
import { BillableType, BookingStatus, BookingType, ServiceType, ShiftSlotStatus } from 'utilities/enum-utils';
import BookingFilterShortcuts from 'views/bookings-v2/bookings-table/components/details/BookingFilterShortcuts';
import BookingErrorIndicator from './BookingErrorIndicator';
import { Center, HoverCard, Table, Tooltip, Text as MantineText, Box, Avatar, Group, Badge } from '@good/ui/core';

type ConditionalLinkProps = PropsWithChildren<{
  wrap: boolean;
  to: string;
}>;

const ConditionalLink: React.FC<ConditionalLinkProps> = ({ wrap, children, to }) => {
  if (!wrap) {
    return <>{children}</>;
  }

  return <Link to={to}>{children}</Link>;
};

export class FieldItem extends PureComponent<{
  bookingItem: IBooking;
  fieldName: string;
  timezone: timeZone;
  showTimezoneIndicator: boolean;
  setFilter?: (filters: IFilter[]) => void;
  useFieldLinks: boolean;
}> {
  override render() {
    const { bookingItem, fieldName, timezone, useFieldLinks } = this.props;
    const displayTimezone = timezone ? timezone : bookingItem.timezone;
    const startDateTime = moment.tz(bookingItem.startDateTime, displayTimezone);
    const endDateTime = moment.tz(bookingItem.endDateTime, displayTimezone);
    const dateLineText = !startDateTime.isSame(endDateTime, 'day')
      ? !startDateTime.isSame(endDateTime, 'month')
        ? !startDateTime.isSame(endDateTime, 'year')
          ? `${startDateTime.format("ddd D MMM 'YY")} - ${endDateTime.format("ddd D MMM 'YY")}`
          : `${startDateTime.format('ddd D MMM')} - ${endDateTime.format("ddd D MMM 'YY")}`
        : `${startDateTime.format('ddd D')} - ${endDateTime.format("ddd D MMM 'YY")}`
      : startDateTime.format("ddd D MMM 'YY");

    const getBookingUri = () => `/bookings/details/${bookingItem.bookingId}`;
    const getCustomerUri = () => `/customer/details/${bookingItem.bookerUserId}`;
    const getTeamMemberUri = () => (bookingItem.workerId ? `/team/details/${bookingItem.workerId}` : '#');

    const getServiceUri = () => {
      const { serviceType, serviceId } = bookingItem;

      if (serviceType === ServiceType.GROUP) {
        return `/group-service/details/${serviceId}`;
      }

      return `/service/details/${serviceId}`;
    };

    switch (fieldName) {
      case 'RECEIVED_DATE':
        return (
          <Table.Td className='fluid-cell'>
            <Text style={{ whiteSpace: 'nowrap' }}>{moment(bookingItem.createdOn).fromNow()}</Text>
          </Table.Td>
        );
      case 'BOOKING_DATE':
        return (
          <>
            <Table.Td className='ph-small' style={{ width: '16px' }}>
              {bookingItem.status === BookingStatus.ARCHIVED ? (
                <Tooltip position='top' label={'This booking has been archived.'}>
                  <Icon type='cloud-server' className='ml-small text-color-secondary' />
                </Tooltip>
              ) : (
                bookingItem.isRecurring && (
                  <Tooltip position='top' label={'This booking is part of a recurring series.'} bg='lavender.9'>
                    <Center ml='sm'>
                      <Timer />
                    </Center>
                  </Tooltip>
                )
              )}
            </Table.Td>
            <Table.Td className='fluid-cell'>
              <ConditionalLink wrap={useFieldLinks} to={getBookingUri()}>
                <div className='pr-small'>
                  <Text style={{ whiteSpace: 'nowrap' }} weight='bold' size='regular'>
                    {dateLineText}
                  </Text>
                  <br />
                  <div className='flex-row'>
                    <Text size='large' color='secondary' style={{ whiteSpace: 'nowrap' }}>
                      {`${startDateTime.format('h:mm a')} – ${endDateTime.format('h:mm a')}`}
                    </Text>
                  </div>
                  {this.props.showTimezoneIndicator && (
                    <TimezoneIndicator showTzName={false} showAbbr={false} timezone={bookingItem.timezone} />
                  )}
                </div>
              </ConditionalLink>
            </Table.Td>
          </>
        );
      case 'BOOKINGS': {
        const dateLineBookingRemoveTeamMemberText = !startDateTime.isSame(endDateTime, 'day')
          ? !startDateTime.isSame(endDateTime, 'month')
            ? !startDateTime.isSame(endDateTime, 'year')
              ? `${startDateTime.format('D MMM YYYY')} - ${endDateTime.format('D MMM YYYY')}`
              : `${startDateTime.format('D MMM')} - ${endDateTime.format('D MMM YYYY')}`
            : `${startDateTime.format('D')} - ${endDateTime.format('D MMM YYYY')}`
          : startDateTime.format('D MMM YYYY');
        return (
          <>
            <Table.Td className='fluid-cell'>
              <Text size={'large'} color={'blue-action'} weight={'bold'}>
                {bookingItem.serviceName}
              </Text>
              <br />
              <div className='pr-small'>
                <Text style={{ whiteSpace: 'nowrap' }} size='regular'>
                  {dateLineBookingRemoveTeamMemberText}
                  {', '}
                  {`${startDateTime.format('h:mm a')} – ${endDateTime.format('h:mm a')}`}
                </Text>
                {this.props.showTimezoneIndicator && (
                  <TimezoneIndicator showTzName={false} showAbbr={false} timezone={bookingItem.timezone} />
                )}
              </div>
            </Table.Td>
          </>
        );
      }
      case 'CUSTOMER':
        return (
          <Table.Td className='ellipsis'>
            <ConditionalLink wrap={useFieldLinks} to={getCustomerUri()}>
              <Group wrap='nowrap'>
                <Avatar
                  src={bookingItem.attachmentUrl}
                  name={`${bookingItem.customerFirstName} ${bookingItem.customerLastName}`}
                />
                {`${bookingItem.customerFirstName} ${bookingItem.customerLastName}`}
                {bookingItem.pinnedAlertNumber > 0 && (
                  <PinnedAlertIcon
                    pinnedAlertNumber={bookingItem.pinnedAlertNumber}
                    customerUserId={bookingItem.bookerUserId}
                  />
                )}
              </Group>
            </ConditionalLink>
          </Table.Td>
        );
      case 'SERVICE':
        return (
          <Table.Td className='ellipsis'>
            <ConditionalLink wrap={useFieldLinks} to={getServiceUri()}>
              <HoverCard position='bottom-end' withinPortal={false} shadow='md' withArrow>
                <HoverCard.Target>
                  <div className='align-center flex-row'>
                    <BookingTypeIcon
                      type={
                        bookingItem.bookingType === BookingType.ACTIVITY_RECORD
                          ? BookingType.ACTIVITY_RECORD
                          : bookingItem.serviceType
                      }
                      className='mr-small'
                    />
                    <MantineText span inherit fw={500}>
                      {bookingItem.serviceName}
                    </MantineText>
                  </div>
                </HoverCard.Target>
                <HoverCard.Dropdown miw={290}>
                  <div>
                    <Text size='x-large' color='secondary' weight='bold'>
                      {bookingItem.serviceName}
                    </Text>
                    <br />
                    <Text>
                      <i>
                        {bookingItem.bookingType === BookingType.ACTIVITY_RECORD
                          ? 'Activity record'
                          : bookingItem.serviceType === ServiceType.GROUP
                            ? 'Group service'
                            : bookingItem.serviceType === ServiceType.INDIVIDUAL
                              ? 'Support service'
                              : 'Support coordination'}
                      </i>
                    </Text>
                    <br />
                    <br />
                    {bookingItem.serviceType === ServiceType.GROUP && (
                      <>
                        {bookingItem.scheduleName &&
                          bookingItem.description &&
                          bookingItem.description !== bookingItem.scheduleName && (
                            <div className='mb-small'>
                              <Text color='tertiary'>Schedule</Text>
                              <br />
                              <Text>{bookingItem.scheduleName}</Text>
                            </div>
                          )}
                        <div>
                          <Text color='tertiary'>Session</Text>
                          <br />
                          <Text>
                            {bookingItem.description
                              ? bookingItem.description
                              : `${moment.tz(bookingItem.sessionStartDateTime, timezone).format('h:mm a')} - ${moment
                                  .tz(bookingItem.sessionEndDateTime, timezone)
                                  .format('h:mm a')}`}
                          </Text>
                        </div>
                        <br />
                      </>
                    )}
                    <div className='align-center flex-row' style={{ gap: '8px' }}>
                      <Text weight='bold'>Status: </Text>
                      {bookingItem.bookingType === BookingType.ACTIVITY_RECORD ? (
                        <>
                          <ActivityRecordStatusTag
                            size='small'
                            className='ml-small'
                            paymentStatus={bookingItem.paymentStatus}
                          />
                        </>
                      ) : (
                        <StatusTag className='ml-small' status={bookingItem.status} size='small' />
                      )}
                    </div>
                  </div>
                </HoverCard.Dropdown>
              </HoverCard>
              <Box mt='xs'>
                {bookingItem.bookingType === BookingType.ACTIVITY_RECORD ? (
                  <>
                    <ActivityRecordStatusTag size='small' paymentStatus={bookingItem.paymentStatus} />
                  </>
                ) : (
                  <StatusTag status={bookingItem.status} size='small' />
                )}
              </Box>
            </ConditionalLink>
          </Table.Td>
        );
      case 'PAYMENT_STATUS':
        return <Table.Td className='ellipsis'>{bookingItem.paymentStatus}</Table.Td>;
      case 'WORKER':
        return (
          <Table.Td className='ellipsis'>
            <ConditionalLink wrap={useFieldLinks && bookingItem?.workerId} to={getTeamMemberUri()}>
              {bookingItem.serviceType === ServiceType.GROUP ? (
                ''
              ) : !_.isEmpty(bookingItem.workerFirstName) ? (
                <Tooltip
                  bg='lavender.9'
                  withArrow
                  label={
                    <>
                      {bookingItem.workerFirstName} {bookingItem.workerLastName} has{' '}
                      {bookingItem.shiftSlotStatus === ShiftSlotStatus.CONFIRMED ? (
                        <b>confirmed</b>
                      ) : (
                        <b>not confirmed</b>
                      )}{' '}
                      this shift.
                    </>
                  }
                >
                  <Group gap='xs' wrap='nowrap'>
                    <Avatar
                      className='mr-small'
                      radius='md'
                      src={bookingItem.workerAttachmentUrl}
                      name={`${bookingItem.workerFirstName} ${bookingItem.workerLastName}`}
                    >
                      {bookingItem.shiftSlotStatus === ShiftSlotStatus.CONFIRMED ? (
                        <Center c='blue'>
                          <CheckCircle />
                        </Center>
                      ) : bookingItem.shiftSlotStatus === ShiftSlotStatus.PENDING ? (
                        <Center c='orange'>
                          <Clock />
                        </Center>
                      ) : null}
                    </Avatar>
                    {`${bookingItem.workerFirstName} ${bookingItem.workerLastName}`}
                  </Group>
                </Tooltip>
              ) : bookingItem.shiftSlotStatus === ShiftSlotStatus.PUBLISHING ||
                bookingItem.shiftSlotStatus === ShiftSlotStatus.PUBLISHED ? (
                <div className='align-center flex-row'>
                  <span className='mr-x-small text-color-rose' style={{ fontSize: '14px', lineHeight: '1' }}>
                    <Broadcast />
                  </span>
                  <TextTag
                    content={'Published'}
                    theme='light'
                    color='rose'
                    size='small'
                    weight='bold'
                    backgroundColor={'white'}
                  />
                </div>
              ) : (
                <Badge color='red' variant='dot' size='md'>
                  No worker assigned
                </Badge>
              )}
            </ConditionalLink>
          </Table.Td>
        );
      case 'TOTAL':
        return (
          <Table.Td style={{ textAlign: 'right' }} className='fluid-cell'>
            {CommonUtils.formatPrice(bookingItem.total)}
          </Table.Td>
        );
      case 'ALERTS':
        return (
          <Table.Td>
            {_.isEmpty(bookingItem.bookingErrors) && bookingItem.billableType === BillableType.ALL_BILLABLE ? (
              <></>
            ) : (
              <BookingErrorIndicator
                bookingErrors={bookingItem.bookingErrors}
                billableType={bookingItem.billableType}
              />
            )}
          </Table.Td>
        );
      case 'FILTER_SHORTCUT':
        return (
          <Table.Td style={{ textAlign: 'right' }} className='mr-small fluid-cell' onClick={(e) => e.stopPropagation()}>
            <BookingFilterShortcuts bookingItem={bookingItem} setFilter={this.props.setFilter} />
          </Table.Td>
        );
      default:
        return <Table.Td />;
    }
  }
}
